import * as React from "react";

import { CreateUserGeneratedSetDialog } from "../../shared/CreateUserGeneratedSetDialog/CreateUserGeneratedSetDialog.js";
import {
  isHubScriptSetEligible,
  isHubScriptScratchTarget,
  resolveHubScriptTarget,
} from "../../../lib/hub-script-target.js";
import type { PersonaOpportunitySet } from "../../../types/persona-opportunities.types.js";
import type { GenerateScriptsTarget } from "../../workspace/generate-scripts/GenerateScriptsModal.js";
import { PersonaGenerateScriptModal } from "../../workspace/generate-script/PersonaGenerateScriptModal.js";

export interface UseOpportunityHubScriptCreateArgs {
  tenantId: string | null;
  sets: PersonaOpportunitySet[];
  selectedSetIds: readonly string[];
  canCreate: boolean;
  onGenerated: () => void;
  /** Called when the opportunity generate dialog closes, so focus can return to its button. */
  onOpportunityClosed?: () => void;
}

export function useOpportunityHubScriptCreate({
  tenantId,
  sets,
  selectedSetIds,
  canCreate,
  onGenerated,
  onOpportunityClosed,
}: UseOpportunityHubScriptCreateArgs) {
  const [open, setOpen] = React.useState(false);
  const [forcedTargets, setForcedTargets] = React.useState<GenerateScriptsTarget[] | null>(null);
  const [pickedSetId, setPickedSetId] = React.useState("");
  const [nameDialogOpen, setNameDialogOpen] = React.useState(false);
  const [createdSet, setCreatedSet] = React.useState<{
    id: string;
    name: string;
    type: "USER_GENERATED";
  } | null>(null);
  const [scratchRequested, setScratchRequested] = React.useState(false);

  const selectedSets = sets.filter((set) => selectedSetIds.includes(set.id));
  const opportunityMode = !scratchRequested && forcedTargets != null;
  const scopedScratchSet =
    scratchRequested &&
    selectedSets.length === 1 &&
    isHubScriptScratchTarget(selectedSets[0])
      ? selectedSets[0]
      : null;
  const targets = forcedTargets ?? [];

  const target = resolveHubScriptTarget(sets, selectedSetIds, "create");
  const activeSetId = scopedScratchSet
    ? scopedScratchSet.id
    : target.showPicker
      ? pickedSetId
      : target.setId;
  const activeSet =
    sets.find((set) => set.id === activeSetId) ??
    (createdSet?.id === activeSetId ? createdSet : undefined);

  React.useEffect(() => {
    if (!createdSet) return;
    if (sets.some((set) => set.id === createdSet.id)) setCreatedSet(null);
  }, [createdSet, sets]);

  const pickerSets = sets
    .filter((set) => isHubScriptSetEligible(set, "create"))
    .map((set) => ({ id: set.id, name: set.name, type: set.type }));
  if (
    createdSet &&
    isHubScriptSetEligible({ id: createdSet.id, type: "USER_GENERATED" }, "create") &&
    !pickerSets.some((set) => set.id === createdSet.id)
  ) {
    pickerSets.push(createdSet);
  }

  const setPicker =
    open && !opportunityMode && !scopedScratchSet && target.showPicker
      ? {
          sets: pickerSets,
          value: pickedSetId,
          onValueChange: setPickedSetId,
          onCreateSet: () => setNameDialogOpen(true),
          isCreatingSet: false,
        }
      : undefined;

  const close = () => {
    const wasOpportunityDialog = forcedTargets != null;
    setOpen(false);
    setForcedTargets(null);
    setScratchRequested(false);
    if (wasOpportunityDialog) onOpportunityClosed?.();
  };

  const dialogs =
    canCreate && tenantId ? (
      <>
        <PersonaGenerateScriptModal
          open={open}
          onClose={close}
          tenantId={tenantId}
          mode={opportunityMode ? "opportunities" : "custom"}
          setId={opportunityMode ? "" : activeSetId}
          setType={activeSet?.type}
          setPicker={setPicker}
          suspendDismiss={nameDialogOpen}
          targets={targets}
          onOpportunityAccepted={onGenerated}
        />
        <CreateUserGeneratedSetDialog
          open={nameDialogOpen}
          onOpenChange={setNameDialogOpen}
          onCreated={(set) => {
            setCreatedSet({ id: set.id, name: set.name, type: "USER_GENERATED" });
            setPickedSetId(set.id);
          }}
        />
      </>
    ) : null;

  return {
    openCreate: canCreate
        ? () => {
            setForcedTargets(null);
            setScratchRequested(true);
            setPickedSetId("");
            setOpen(true);
          }
      : undefined,
    openOpportunityGenerate: canCreate
      ? (next: GenerateScriptsTarget[]) => {
          setScratchRequested(false);
          setForcedTargets(next);
          setOpen(true);
        }
      : undefined,
    dialogs,
  };
}
